import React, { useState, useEffect } from 'react';
import { settingsAPI, messagingAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  MessageCircle, MessagesSquare, Eye, EyeOff, CheckCircle, XCircle,
  Loader, Save, RefreshCw, Send,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const MASKED = '••••••••';

const SECTION = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{
      fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14,
      paddingBottom: 6, borderBottom: '1px solid var(--border)'
    }}>{title}</div>
    {children}
  </div>
);

const FIELD = ({ label, hint, required, children }) => (
  <div className="form-group">
    <label className="form-label">{label}{required && <span className="required"> *</span>}</label>
    {children}
    {hint && <div className="form-hint">{hint}</div>}
  </div>
);

function SecretInput({ value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', maxWidth: 480 }}>
      <input
        className="form-control font-mono"
        type={show ? 'text' : 'password'}
        value={value}
        onFocus={() => { if (value === MASKED) onChange(''); }}
        onBlur={() => { if (value === '') onChange(MASKED); }}
        onChange={e => onChange(e.target.value)}
        style={{ paddingRight: 40 }}
      />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

// ─── WhatsApp tab ─────────────────────────────────────────────────────────────
function WhatsAppTab() {
  const empty = {
    provider: 'meta_cloud', phoneNumberId: '', businessAccountId: '',
    accessToken: '', apiUrl: '', webhookVerifyToken: '', isEnabled: false,
  };
  const [wa, setWaState] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const setWa = (k, v) => setWaState(f => ({ ...f, [k]: v }));

  useEffect(() => {
    settingsAPI.getWhatsApp()
      .then(r => setWaState(f => ({ ...empty, ...r.data.data, provider: 'meta_cloud' })))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await settingsAPI.saveWhatsApp(wa);
      toast.success('WhatsApp settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await settingsAPI.testWhatsApp();
      setTestResult('success');
      toast.success(res.data?.message || 'Connected');
    } catch (err) {
      setTestResult('failed');
      toast.error(err.response?.data?.message || 'Connection test failed');
    } finally { setTesting(false); }
  };

  return (
    <div className="card"><div className="card-body">
      <SECTION title="Provider Type">
        <FIELD label="Provider" hint="Meta's official WhatsApp Cloud API">
          <select className="form-control" style={{ maxWidth: 300 }} value="meta_cloud" disabled>
            <option value="meta_cloud">WhatsApp Cloud API (Meta official)</option>
          </select>
        </FIELD>
      </SECTION>

      {(
        <SECTION title="Meta Cloud API Credentials">
          <div className="form-grid">
            <FIELD label="Phone Number ID" required hint="From Meta for Developers → WhatsApp → API Setup">
              <input className="form-control font-mono" value={wa.phoneNumberId}
                onChange={e => setWa('phoneNumberId', e.target.value.trim())} placeholder="e.g. 109876543210123" />
            </FIELD>
            <FIELD label="Business Account ID" hint="Optional — WABA ID for template management">
              <input className="form-control font-mono" value={wa.businessAccountId}
                onChange={e => setWa('businessAccountId', e.target.value.trim())} placeholder="e.g. 987654321098765" />
            </FIELD>
          </div>
          <FIELD label="Access Token" required hint={wa.accessToken === MASKED ? 'Leave unchanged to keep current token' : 'Permanent token from a System User'}>
            <SecretInput value={wa.accessToken} onChange={v => setWa('accessToken', v)} />
          </FIELD>
          <FIELD label="Webhook Verify Token" hint="Optional — shared secret used when Meta calls your webhook">
            <input className="form-control" value={wa.webhookVerifyToken}
              onChange={e => setWa('webhookVerifyToken', e.target.value)} style={{ maxWidth: 320 }} />
          </FIELD>
        </SECTION>
      )}

      <SECTION title="Status">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
          <input type="checkbox" checked={wa.isEnabled} onChange={e => setWa('isEnabled', e.target.checked)} />
          Enable WhatsApp API integration (uncheck to fall back to wa.me deep links)
        </label>
      </SECTION>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn btn-secondary btn-sm" onClick={test} disabled={testing}>
          {testing
            ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Testing…</>
            : testResult === 'success'
              ? <><CheckCircle size={12} style={{ marginRight: 5, color: 'var(--success)' }} />Connected</>
              : testResult === 'failed'
                ? <><XCircle size={12} style={{ marginRight: 5, color: 'var(--danger)' }} />Failed</>
                : <><RefreshCw size={12} style={{ marginRight: 5 }} />Test Connection</>}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={14} style={{ marginRight: 6 }} />{saving ? 'Saving…' : 'Save WhatsApp Settings'}
        </button>
      </div>
    </div></div>
  );
}

// ─── SMS tab ──────────────────────────────────────────────────────────────────
export function SmsTab() {
  const empty = { provider: 'fast2sms', apiKey: '', senderId: '', route: '', urlTemplate: '', isEnabled: false };
  const [sms, setSmsState] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMobile, setTestMobile] = useState('');
  const setSms = (k, v) => setSmsState(f => ({ ...f, [k]: v }));

  useEffect(() => {
    messagingAPI.getSmsSettings()
      .then(r => setSmsState(f => ({ ...empty, ...r.data.data })))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await messagingAPI.saveSmsSettings(sms);
      toast.success('SMS settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const test = async () => {
    if (!testMobile.trim()) return toast.error('Enter a mobile number to send the test SMS to');
    setTesting(true);
    try {
      const res = await messagingAPI.testSms(testMobile.trim());
      toast.success(res.data?.message || 'Test SMS sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Test SMS failed');
    } finally { setTesting(false); }
  };

  return (
    <div className="card"><div className="card-body">
      <div style={{ background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '10px 14px', marginBottom: 22, fontSize: '.83rem', color: 'var(--c-92400e)' }}>
        SMS reaches customers even when their internet is <b>down</b> — configure it so network-down alerts always get delivered.
      </div>

      <SECTION title="Provider Type">
        <FIELD label="SMS Provider">
          <select className="form-control" style={{ maxWidth: 300 }} value={sms.provider}
            onChange={e => setSms('provider', e.target.value)}>
            <option value="fast2sms">Fast2SMS</option>
            <option value="msg91">MSG91</option>
            <option value="custom">Custom HTTP Gateway</option>
          </select>
        </FIELD>
      </SECTION>

      {sms.provider === 'fast2sms' && (
        <SECTION title="Fast2SMS Credentials">
          <FIELD label="API Key" required hint={sms.apiKey === MASKED ? 'Leave unchanged to keep current key' : 'From fast2sms.com → Dev API'}>
            <SecretInput value={sms.apiKey} onChange={v => setSms('apiKey', v)} />
          </FIELD>
          <FIELD label="Route" hint="q = Quick SMS (default, no DLT needed) · dlt = DLT approved route">
            <input className="form-control" value={sms.route} onChange={e => setSms('route', e.target.value.trim())}
              placeholder="q" style={{ maxWidth: 160 }} />
          </FIELD>
        </SECTION>
      )}

      {sms.provider === 'msg91' && (
        <SECTION title="MSG91 Credentials">
          <FIELD label="Auth Key" required hint={sms.apiKey === MASKED ? 'Leave unchanged to keep current key' : 'From msg91.com dashboard'}>
            <SecretInput value={sms.apiKey} onChange={v => setSms('apiKey', v)} />
          </FIELD>
          <div className="form-grid">
            <FIELD label="Sender ID" hint="6-char DLT approved sender, e.g. LXMCBL">
              <input className="form-control font-mono" value={sms.senderId}
                onChange={e => setSms('senderId', e.target.value.trim().toUpperCase())} maxLength={6} />
            </FIELD>
            <FIELD label="Route" hint="4 = transactional (default)">
              <input className="form-control" value={sms.route} onChange={e => setSms('route', e.target.value.trim())}
                placeholder="4" style={{ maxWidth: 120 }} />
            </FIELD>
          </div>
        </SECTION>
      )}

      {sms.provider === 'custom' && (
        <SECTION title="Custom Gateway">
          <FIELD label="URL Template" required
            hint={'Full GET URL with {mobile} and {message} placeholders, e.g. https://sms.example.com/send?key=XXX&to={mobile}&text={message}'}>
            <input className="form-control font-mono" value={sms.urlTemplate}
              onChange={e => setSms('urlTemplate', e.target.value.trim())} />
          </FIELD>
        </SECTION>
      )}

      <SECTION title="Status">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
          <input type="checkbox" checked={sms.isEnabled} onChange={e => setSms('isEnabled', e.target.checked)} />
          Enable SMS gateway
        </label>
      </SECTION>

      <SECTION title="Send Test SMS">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-control font-mono" value={testMobile} onChange={e => setTestMobile(e.target.value)}
            placeholder="10-digit mobile" style={{ maxWidth: 200 }} />
          <button className="btn btn-secondary btn-sm" onClick={test} disabled={testing}>
            {testing
              ? <><Loader size={12} style={{ marginRight: 5, animation: 'spin 1s linear infinite' }} />Sending…</>
              : <><Send size={12} style={{ marginRight: 5 }} />Send Test SMS</>}
          </button>
          <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Save settings first, then test</span>
        </div>
      </SECTION>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={14} style={{ marginRight: 6 }} />{saving ? 'Saving…' : 'Save SMS Settings'}
        </button>
      </div>
    </div></div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MessagingApiPage() {
  useDarkReady();
  const [tab, setTab] = useState('whatsapp');
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">WhatsApp / SMS API <span style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--text-muted)' }}>· Meta Cloud (not used)</span></h1>
          <p className="page-subtitle">Connect your WhatsApp Business API and SMS gateway for customer messaging</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {[
          { key: 'whatsapp', label: 'WhatsApp API', icon: MessageCircle },
          { key: 'sms', label: 'SMS Gateway', icon: MessagesSquare },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-secondary'}`}>
            <t.icon size={14} style={{ marginRight: 6 }} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'whatsapp' ? <WhatsAppTab /> : <SmsTab />}
    </div>
  );
}
